import React from "react";
import { motion } from "framer-motion";
import { ScrollText, ShieldAlert, Users, Timer, Trophy, Activity, Info } from "lucide-react";

const SECTIONS = [
  {
    id: "duree",
    icon: Timer,
    title: "Durée des parties",
    items: [
      "Cinq (5) parties assurées, la 5e partie peut être une partie éliminatoire en fonction du nombre d'équipes inscrites dans cette catégorie.",
      "Une période de trois minutes d'échauffement sera accordée.",
      "Deux périodes de dix minutes et une période de douze minutes chronométrées.",
      "La glace sera faite après chaque partie.",
      "Seuls les joueurs désignés par l'entraîneur se présenteront sur la patinoire. (En cas de retard, le tournoi se garde le privilège de modifier ce règlement).",
      "Il n'y a pas de période de prolongation lors des rondes préliminaires (les quatre premiers matchs).",
      "Un temps d'arrêt de trente secondes sera accordé en tout temps durant le tournoi, 1 fois par partie par équipe.",
    ],
  },
  {
    id: "prolongation",
    icon: Activity,
    title: "Prolongation",
    items: [
      "Seulement lors des demi-finales et finales, une période de cinq (5) minutes chronométrées à 5 contre 5 sera jouée.",
      "Si l'égalité persiste après la prolongation, il y aura fusillade à 3 joueurs par équipe; si l'égalité persiste, un joueur de chaque côté.",
      "Un joueur ne peut pas revenir tirer avant que TOUS LES JOUEURS de l'équipe aient tenté leur chance.",
    ],
  },
  {
    id: "egalite",
    icon: Trophy,
    title: "Départage d'égalité au classement",
    intro: "Si toutes les équipes s'affrontent dans un même pool :",
    ordered: [
      "Le plus grand nombre de victoires.",
      "Le moins de défaites.",
      "Le moins de buts contre.",
      "Le résultat entre les deux équipes (si toutes les équipes du pool s'affrontent).",
      "Différentiel : buts pour moins les buts contre.",
      "Le plus de buts pour.",
      "L'équipe ayant écopé du moins grand nombre de minutes de pénalités pendant les préliminaires.",
      "Tirage au sort.",
    ],
    note: "Si toutes les équipes ne s'affrontent pas dans un même pool et qu'il y a 3 équipes ou plus à égalité, les étapes 4 et 7 sont exclues.",
  },
  {
    id: "ecarts",
    icon: Activity,
    title: "Écarts de buts",
    items: [
      "Le temps devient continu avec un écart de six (6) buts. Si l'écart est réduit à 5 buts, le temps redevient chronométré.",
      "Pour le différentiel +/-, l'écart maximum pour une partie est de 7 buts (une équipe peut avoir un maximum de +7 et un minimum de –7 par partie).",
    ],
  },
  {
    id: "formation",
    icon: Users,
    title: "Formation des équipes",
    items: [
      "Tous les joueurs doivent fournir, sur demande, une carte d'assurance maladie.",
      "Toute fausse information sur un joueur peut entraîner la suspension du joueur et de l'entraîneur pour le reste du tournoi. Selon la situation, l'équipe de ce joueur peut être disqualifiée.",
      "21 joueurs pourront être inscrits sur la liste, dont trois gardiens.",
      "Un maximum de 15 joueurs peut être habillé pour la partie.",
      "Le nombre minimum pour débuter une partie est de 4 + 1 gardien.",
      "Pour un tournoi avec 4 parties préliminaires (pool), un joueur doit avoir disputé deux parties à la ronde pour prendre part aux demi-finales et finales.",
    ],
  },
  {
    id: "penalites",
    icon: ShieldAlert,
    title: "Mise en échec, pénalités majeures et mineures",
    items: [
      "La pénalité mineure sera de 1 minute 30 secondes et la pénalité majeure de 4 minutes.",
      "La mise en échec par derrière sera pénalisée par une expulsion du joueur.",
      "Selon la gravité du geste, l'officiel a la possibilité d'attribuer une pénalité de 1:30 + 10 minutes entraînant 1 partie de suspension OU une majeure de 4 minutes + 10 minutes entraînant une suspension automatique de 3 parties.",
      "Aucune expulsion ou suspension ne pourra être négociée.",
    ],
  },
  {
    id: "autres",
    icon: Info,
    title: "Autres règlements",
    items: [
      "Les visiteurs portent le chandail pâle et les locaux le chandail foncé. Si une équipe n'a qu'une seule couleur de chandail, un arrangement à l'amiable serait apprécié avec l'équipe adverse. Si les couleurs portent à confusion, l'équipe visiteuse devra changer son chandail.",
      "Soyez présents 45 minutes avant la partie et présentez-vous au registraire ou au marqueur, pour toutes les parties du tournoi.",
      "L'heure de la partie peut être devancée de quinze (15) minutes — soyez prêts à sauter sur la glace dans ces délais.",
      "Vos chambres seront indiquées sur l'écran à l'entrée ou par la personne responsable de votre arrivée.",
      "Vous devez libérer la chambre dans un délai de 30 minutes après la partie.",
      "Seuls les entraîneurs autorisés peuvent circuler dans les corridors des chambres. Merci de votre collaboration et d'informer vos parents.",
      "Lors des finales, l'emplacement sur l'horaire indiquera le visiteur et le local.",
    ],
  },
];

const Reglements = () => {
  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="reglements-page">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-8 sm:py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Tournoi · Édition 2026
          </span>
          <h1 className="mt-2 sm:mt-3 font-display text-4xl sm:text-6xl lg:text-8xl uppercase leading-none">
            Règlements <span className="text-[#E60012]">officiels</span>
          </h1>
          <p className="mt-5 text-white/65 max-w-3xl text-sm sm:text-base leading-relaxed">
            Le port de l'équipement protecteur complet est obligatoire,{" "}
            <span className="text-white">incluant le protège-cou</span>. Aucun mesurage de bâton ne
            sera effectué. Aucun protêt ne sera accepté. Tout joueur ou entraîneur expulsé peut être
            rencontré par le comité de discipline selon le rapport de l'arbitre ou à la demande du
            superviseur des arbitres.
          </p>
          <p className="mt-3 text-white/55 max-w-3xl text-sm leading-relaxed">
            La direction du tournoi se garde le privilège de modifier ou d'ajouter certains
            règlements. La direction se garde également le privilège de changer une équipe de
            catégorie si elle ne respecte pas les règlements de sa catégorie ou si elle n'est pas
            assez compétitive pour évoluer dans la catégorie inscrite.
          </p>
        </div>
      </section>

      {/* TABLE OF CONTENTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="bg-[#111] border border-white/10 p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <ScrollText className="w-4 h-4 text-[#E60012]" />
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-white/65">
              Sommaire
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`reglements-toc-${s.id}`}
                className="group flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#E60012] hover:bg-[#E60012]/10 transition-colors text-sm font-condensed"
              >
                <s.icon className="w-3.5 h-3.5 text-[#E60012] shrink-0" />
                <span className="text-white/80 group-hover:text-white truncate">{s.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 space-y-10">
        {SECTIONS.map((s, i) => (
          <motion.article
            key={s.id}
            id={s.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: i * 0.05 }}
            className="bg-[#111] border-l-2 border-[#E60012] scroll-mt-24"
            data-testid={`reglements-section-${s.id}`}
          >
            <div className="px-5 sm:px-7 py-5 border-b border-white/10 flex items-center gap-3">
              <s.icon className="w-5 h-5 text-[#E60012]" />
              <h2 className="font-display text-2xl sm:text-3xl uppercase">{s.title}</h2>
            </div>
            <div className="px-5 sm:px-7 py-6 space-y-3">
              {s.intro && <p className="text-white/70 text-sm leading-relaxed">{s.intro}</p>}
              {s.items && (
                <ul className="space-y-2.5">
                  {s.items.map((it) => (
                    <li
                      key={it.slice(0, 30)}
                      className="flex gap-3 text-sm sm:text-[15px] text-white/80 leading-relaxed"
                    >
                      <span className="text-[#E60012] mt-1.5 shrink-0">▸</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.ordered && (
                <ol className="space-y-2.5 list-none counter-reset-rules">
                  {s.ordered.map((it, idx) => (
                    <li
                      key={it.slice(0, 30)}
                      className="flex gap-3 text-sm sm:text-[15px] text-white/80 leading-relaxed"
                    >
                      <span className="font-display text-[#E60012] text-lg shrink-0 w-7 leading-none">
                        {idx + 1}.
                      </span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ol>
              )}
              {s.note && (
                <div className="mt-4 px-4 py-3 bg-black/40 border border-white/10 text-xs sm:text-sm text-white/55 leading-relaxed">
                  <span className="text-[#E60012] font-condensed uppercase tracking-wider mr-2">
                    Note
                  </span>
                  {s.note}
                </div>
              )}
            </div>
          </motion.article>
        ))}

        {/* RESPECT DES ARBITRES */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-[#1a0306] to-[#0a0a0a] border border-[#E60012]/40 p-7 sm:p-10 clip-jersey"
          data-testid="reglements-respect"
        >
          <div className="flex items-center gap-3 mb-3">
            <ShieldAlert className="w-5 h-5 text-[#E60012]" />
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
              Esprit du tournoi
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl uppercase leading-tight">
            Veuillez respecter <span className="text-[#E60012]">les arbitres</span> en tout temps !
          </h3>
          <p className="mt-4 text-white/70 text-sm sm:text-base leading-relaxed max-w-3xl">
            Si vous avez une situation pour laquelle vous aimeriez discuter, un arbitre en chef est
            assigné au tournoi pour vous assister. Notez toutefois qu'il n'a pas le pouvoir de
            changer une décision prise lors d'une partie. Nous avons avisé nos arbitres de
            respecter les instructeurs et nous espérons la même chose en retour.
          </p>
          <p className="mt-5 font-display text-2xl uppercase text-white">
            Bonne chance et bon tournoi ! <span className="text-[#E60012]">— Coupe CH</span>
          </p>
        </motion.div>
      </section>
    </div>
  );
};

export default Reglements;
